/**
 * Section title injection utilities for homepage/section pages
 *
 * Now uses the shared debouncedObserver utility.
 *
 * @module lib/sectionInjection
 */

const { logInfo, logWarn } = require('./logger');
const { createDebouncedObserver } = require('./utils/debouncedObserver');

/**
 * Get all section title elements from the DOM
 * @returns {HTMLElement[]} Array of section title elements
 */
function getSectionTitleElements() {
  if (typeof document === 'undefined') return [];
  const divMatches = Array.from(document.querySelectorAll('div.section__title'));
  if (divMatches.length > 0) return divMatches;
  return Array.from(document.querySelectorAll('.section__title'));
}

/**
 * Select sparse indices from a range for module insertion
 * @param {number} total - Total number of elements
 * @param {number[]} preferredIndices - Preferred indices to use
 * @param {number} maxInsertions - Maximum number of insertions
 * @returns {number[]} Selected indices
 */
function selectSparseIndices(total, preferredIndices, maxInsertions) {
  const indices = [];
  const used = new Set();
  const max = Math.max(0, Number.isFinite(maxInsertions) ? maxInsertions : 0);

  const preferred = Array.isArray(preferredIndices) ? preferredIndices : [];
  for (const idx of preferred) {
    if (indices.length >= max) break;
    const n = Number(idx);
    if (!Number.isFinite(n)) continue;
    if (n < 0 || n >= total) continue;
    if (used.has(n)) continue;
    used.add(n);
    indices.push(n);
  }

  if (indices.length === 0 && total > 0 && max > 0) {
    const mid = Math.max(0, Math.min(total - 1, Math.floor(total / 2)));
    used.add(mid);
    indices.push(mid);
  }

  if (indices.length < max && total > 0) {
    const last = total - 1;
    if (!used.has(last)) {
      indices.push(last);
    }
  }

  return indices.slice(0, max);
}

/**
 * Whether inserting above this element would move content the reader can see:
 * true when its top is above the bottom of the viewport.
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function startsAboveViewportBottom(el) {
  if (typeof window === 'undefined' || !el || typeof el.getBoundingClientRect !== 'function') {
    return false;
  }
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  return el.getBoundingClientRect().top < viewportHeight;
}

// Registry for active observers
const sectionTitleInjectionObservers = new Map();

/**
 * Ensure a MutationObserver is set up for section title injection
 * @param {string} key - Unique key for this observer
 * @param {function} callback - Callback to run when mutations detected
 */
function ensureSectionTitleInjectionObserver(key, callback) {
  if (typeof document === 'undefined') return;
  if (typeof MutationObserver === 'undefined') return;
  if (!key || typeof callback !== 'function') return;
  if (sectionTitleInjectionObservers.has(key)) return;

  const observer = createDebouncedObserver(callback, {
    debounceMs: 75,
    errorContext: 'Section title injection observer callback'
  });

  if (!observer) return;

  try {
    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true
    });
    sectionTitleInjectionObservers.set(key, observer);
  } catch (err) {
    logWarn('Failed to start section title injection observer', err);
  }
}

/**
 * Inject modules above `.section__title` blocks on homepage/section pages.
 *
 * Safe to call repeatedly (it marks targets to avoid duplicates).
 *
 * @param {object} options
 * @param {string} options.moduleKey - Identifier used for idempotency (e.g. 'newsletter', 'support')
 * @param {string} options.locationPrefix - Location prefix used for iframe query params
 * @param {number} [options.maxInsertions]
 * @param {number[]} [options.preferredIndices]
 * @param {(location: string, targetIndex: number) => HTMLElement|null} options.createModule
 * @returns {number} Count of inserted modules
 */
function injectModulesBeforeSectionTitles(options) {
  if (typeof document === 'undefined') return 0;
  if (!options || typeof options !== 'object') return 0;

  const moduleKey = typeof options.moduleKey === 'string' && options.moduleKey.trim()
    ? options.moduleKey.trim()
    : 'module';
  const markerClass = `milla-${moduleKey}-before-section-title`;

  const locationPrefix =
    typeof options.locationPrefix === 'string' && options.locationPrefix.trim()
      ? options.locationPrefix.trim()
      : 'homepage_section_title';

  const createModule = options.createModule;
  if (typeof createModule !== 'function') return 0;

  const allTitles = getSectionTitleElements()
    .filter((el) => el && el.parentNode)
    .filter((el, idx, arr) => arr.indexOf(el) === idx);

  if (allTitles.length === 0) return 0;

  const observe = options.observe === true;
  if (observe) {
    const observeKey = `section-title:${moduleKey}:${locationPrefix}`;
    ensureSectionTitleInjectionObserver(observeKey, () => injectModulesBeforeSectionTitles({
      ...options,
      observe: false
    }));
  }

  const maxInsertions = Number.isFinite(options.maxInsertions) ? options.maxInsertions : 2;
  const preferredIndices = Array.isArray(options.preferredIndices) ? options.preferredIndices : [];
  const targetIndices = selectSparseIndices(allTitles.length, preferredIndices, maxInsertions);

  let inserted = 0;

  for (const idx of targetIndices) {
    const titleEl = allTitles[idx];
    if (!titleEl || !titleEl.parentNode) continue;

    const titleMarkerAttr = `data-milla-${moduleKey}-injected`;
    if (titleEl.getAttribute(titleMarkerAttr) === 'true') {
      continue;
    }

    const prev = titleEl.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains(markerClass)) {
      titleEl.setAttribute(titleMarkerAttr, 'true');
      continue;
    }

    // Inserting above a title the reader can see, or has scrolled past, shifts
    // the page. Such titles stay unmarked for later passes.
    if (startsAboveViewportBottom(titleEl)) continue;

    const location = `${locationPrefix}_${idx + 1}`;
    const moduleEl = createModule(location, idx);
    if (!moduleEl) continue;

    if (moduleEl.classList) {
      moduleEl.classList.add(markerClass);
    }
    try {
      moduleEl.setAttribute('data-milla-location', location);
    } catch (_) {
      // ignore
    }

    try {
      if (typeof titleEl.insertAdjacentElement === 'function') {
        titleEl.insertAdjacentElement('beforebegin', moduleEl);
      } else {
        titleEl.parentNode.insertBefore(moduleEl, titleEl);
      }
    } catch (_) {
      titleEl.parentNode.insertBefore(moduleEl, titleEl);
    }
    titleEl.setAttribute(titleMarkerAttr, 'true');
    inserted += 1;
  }

  if (typeof window !== 'undefined' && window._millaDebugEmbeds === true) {
    logInfo('Section title injection result', {
      moduleKey,
      locationPrefix,
      titles: allTitles.length,
      inserted
    });
  }

  return inserted;
}

module.exports = {
  getSectionTitleElements,
  selectSparseIndices,
  injectModulesBeforeSectionTitles
};
